var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};

// src/config.ts
var DEFAULT_HOST = "https://pay.coinbase.com";

// src/onramp/generateOnRampURL.ts
var generateOnRampURL = /* @__PURE__ */ __name(({ host = DEFAULT_HOST, destinationWallets, ...otherParams }) => {
  const url = new URL(host);
  url.pathname = "/buy/select-asset";
  url.searchParams.append("destinationWallets", JSON.stringify(destinationWallets));
  Object.keys(otherParams).forEach((key) => {
    const value = otherParams[key];
    if (value !== void 0) {
      url.searchParams.append(key, value.toString());
    }
  });
  url.searchParams.sort();
  return url.toString();
}, "generateOnRampURL");

// src/utils/createEmbeddedContent.ts
var EMBEDDED_IFRAME_ID = "cbpay-embedded-onramp";
var createEmbeddedContent = /* @__PURE__ */ __name(({ url, width = "100%", height = "100%", position = "fixed", top = "0px" }) => {
  const iframe = document.createElement("iframe");
  iframe.style.border = "unset";
  iframe.style.borderWidth = "0";
  iframe.style.width = width.toString();
  iframe.style.height = height.toString();
  iframe.style.position = position;
  iframe.style.top = top;
  iframe.id = EMBEDDED_IFRAME_ID;
  iframe.src = url;
  return iframe;
}, "createEmbeddedContent");

// src/utils/postMessage.ts
var MessageCodes;
(function(MessageCodes2) {
  MessageCodes2["LaunchEmbedded"] = "launch_embedded";
  MessageCodes2["AppReady"] = "app_ready";
  MessageCodes2["AppParams"] = "app_params";
  MessageCodes2["SigninSuccess"] = "signin_success";
  MessageCodes2["Success"] = "success";
  MessageCodes2["Exit"] = "exit";
  MessageCodes2["Event"] = "event";
  MessageCodes2["Error"] = "error";
  MessageCodes2["PixelReady"] = "pixel_ready";
  MessageCodes2["OnAppParamsNonce"] = "on_app_params_nonce";
})(MessageCodes || (MessageCodes = {}));
var onBroadcastedPostMessage = /* @__PURE__ */ __name((messageCode, { onMessage: callback, shouldUnsubscribe = true, allowedOrigin, onValidateOrigin = /* @__PURE__ */ __name(() => Promise.resolve(true), "onValidateOrigin") }) => {
  const onMessage = /* @__PURE__ */ __name((e) => {
    const { eventName, data } = parsePostMessage(e.data);
    const isOriginAllowed = !allowedOrigin || e.origin === allowedOrigin;
    if (eventName === messageCode) {
      void (async () => {
        if (isOriginAllowed && await onValidateOrigin(e.origin)) {
          callback(data);
          if (shouldUnsubscribe) {
            window.removeEventListener("message", onMessage);
          }
        }
      })();
    }
  }, "onMessage");
  window.addEventListener("message", onMessage);
  return () => {
    window.removeEventListener("message", onMessage);
  };
}, "onBroadcastedPostMessage");
var getSdkTarget = /* @__PURE__ */ __name((win) => {
  if (win !== window) {
    return win;
  } else if (isMobileSdkTarget(win)) {
    return {
      postMessage: (message) => win.ReactNativeWebView.postMessage(message)
    };
  } else if (win.opener) {
    return win.opener;
  } else if (win.parent !== win.self) {
    return win.parent;
  } else {
    return void 0;
  }
}, "getSdkTarget");
var isMobileSdkTarget = /* @__PURE__ */ __name((win) => {
  try {
    return win.ReactNativeWebView?.postMessage !== void 0;
  } catch {
    return false;
  }
}, "isMobileSdkTarget");
var broadcastPostMessage = /* @__PURE__ */ __name((win, eventName, { allowedOrigin = "*", data } = {}) => {
  const message = formatPostMessage(eventName, data);
  win.postMessage(message, allowedOrigin);
}, "broadcastPostMessage");
var parsePostMessage = /* @__PURE__ */ __name((data) => {
  try {
    return JSON.parse(data);
  } catch {
    return {
      eventName: data
    };
  }
}, "parsePostMessage");
var formatPostMessage = /* @__PURE__ */ __name((eventName, data) => {
  if (data) {
    return JSON.stringify({
      eventName,
      data
    });
  }
  return eventName;
}, "formatPostMessage");

// src/utils/CoinbasePixel.ts
var PIXEL_PATH = "/embed";
var DEFAULT_MAX_LOAD_TIMEOUT = 5e3;
var PIXEL_ID = "coinbase-sdk-connect";
var PopupSizes = {
  signin: {
    width: 460,
    height: 730
  },
  widget: {
    width: 430,
    height: 600
  }
};
var CoinbasePixel = class {
  constructor({ host = DEFAULT_HOST, appId, appParams, onReady, onFallbackOpen, debug }) {
    __publicField(this, "state", "loading");
    __publicField(this, "nonce", "");
    __publicField(this, "eventStreamListeners", {});
    __publicField(this, "unsubs", []);
    __publicField(this, "isLoggedIn", false);
    __publicField(this, "openExperience", /* @__PURE__ */ __name((options) => {
      this.log("Attempting to open experience", {
        state: this.state
      });
      if (this.state === "waiting_for_response") {
        return;
      }
      if (this.state === "loading") {
        return;
      }
      if (this.state === "failed") {
        this.onFallbackOpen?.();
        return;
      }
      if (!this.nonce) {
        throw new Error("Attempted to open CB Pay experience without nonce");
      }
      const nonce = this.nonce;
      this.nonce = "";
      this.setupExperienceListeners(options);
      const { path, experienceLoggedIn, experienceLoggedOut, embeddedContentStyles } = options;
      const widgetUrl = new URL(`${this.host}${path}`);
      widgetUrl.searchParams.append("appId", this.appId);
      widgetUrl.searchParams.append("type", "secure_standalone");
      const experience = this.isLoggedIn ? experienceLoggedIn : experienceLoggedOut || experienceLoggedIn;
      widgetUrl.searchParams.append("nonce", nonce);
      const url = widgetUrl.toString();
      this.log("Opening experience", {
        experience,
        isLoggedIn: this.isLoggedIn
      });
      if (experience === "embedded") {
        const openEmbeddedExperience = /* @__PURE__ */ __name(() => {
          const embedded = createEmbeddedContent({
            url,
            ...embeddedContentStyles
          });
          if (embeddedContentStyles?.target) {
            document.querySelector(embeddedContentStyles?.target)?.replaceChildren(embedded);
          } else {
            document.body.appendChild(embedded);
          }
        }, "openEmbeddedExperience");
        if (!this.isLoggedIn) {
          this.startDirectSignin(openEmbeddedExperience);
        } else {
          openEmbeddedExperience();
        }
      } else if (experience === "popup" && window.chrome?.windows?.create) {
        void window.chrome.windows.create({
          url,
          setSelfAsOpener: true,
          type: "popup",
          focused: true,
          width: PopupSizes.signin.width,
          height: PopupSizes.signin.height,
          left: window.screenLeft - PopupSizes.signin.width - 10,
          top: window.screenTop
        }, (winRef) => {
          this.addEventStreamListener("open", () => {
            if (winRef?.id) {
              chrome.windows.update(winRef.id, {
                width: PopupSizes.widget.width,
                height: PopupSizes.widget.height,
                left: window.screenLeft - PopupSizes.widget.width - 10,
                top: window.screenTop
              });
            }
          });
        });
      } else if (experience === "new_tab" && window.chrome?.tabs?.create) {
        void window.chrome.tabs.create({
          url
        });
      } else {
        openWindow(url, experience);
      }
      const onOpen = /* @__PURE__ */ __name(() => {
        this.sendAppParams();
        this.removeEventStreamListener("open", onOpen);
      }, "onOpen");
      this.addEventStreamListener("open", onOpen);
    }, "openExperience"));
    __publicField(this, "endExperience", /* @__PURE__ */ __name(() => {
      document.getElementById(EMBEDDED_IFRAME_ID)?.remove();
    }, "endExperience"));
    __publicField(this, "destroy", /* @__PURE__ */ __name(() => {
      document.getElementById(PIXEL_ID)?.remove();
      this.unsubs.forEach((unsub) => unsub());
    }, "destroy"));
    __publicField(this, "addPixelReadyListener", /* @__PURE__ */ __name(() => {
      this.onMessage("pixel_ready", {
        shouldUnsubscribe: false,
        onMessage: (data) => {
          this.log("Received message: pixel_ready");
          this.isLoggedIn = !!data?.isLoggedIn;
          this.removeErrorListener?.();
          this.sendAppParams(() => {
            this.onReadyCallback?.();
          });
        }
      });
    }, "addPixelReadyListener"));
    __publicField(this, "addErrorListener", /* @__PURE__ */ __name(() => {
      this.removeErrorListener = this.onMessage("error", {
        shouldUnsubscribe: true,
        onMessage: (data) => {
          this.log("Received message: error");
          if (data) {
            const message = typeof data === "string" ? data : JSON.stringify(data);
            this.onReadyCallback?.(new Error(message));
          }
        }
      });
    }, "addErrorListener"));
    __publicField(this, "embedPixel", /* @__PURE__ */ __name(() => {
      document.getElementById(PIXEL_ID)?.remove();
      const pixel = createPixel({
        host: this.host,
        appId: this.appId
      });
      pixel.onerror = this.onFailedToLoad;
      this.pixelIframe = pixel;
      document.body.appendChild(pixel);
    }, "embedPixel"));
    __publicField(this, "onFailedToLoad", /* @__PURE__ */ __name(() => {
      this.state = "failed";
      if (this.onFallbackOpen) {
        if (this.debug) {
          console.warn("Failed to load CB Pay pixel. Falling back to opening in new tab.");
        }
        this.onReadyCallback?.();
      } else {
        const error = new Error("Failed to load CB Pay pixel");
        if (this.debug) {
          console.error(error);
        }
        this.onReadyCallback?.(error);
      }
    }, "onFailedToLoad"));
    __publicField(this, "sendAppParams", /* @__PURE__ */ __name((callback) => {
      if (this.pixelIframe?.contentWindow) {
        this.log("Sending message: app_params");
        this.onMessage("on_app_params_nonce", {
          onMessage: (data) => {
            this.state = "ready";
            this.nonce = data?.nonce || "";
            callback?.();
          }
        });
        this.state = "waiting_for_response";
        broadcastPostMessage(this.pixelIframe.contentWindow, "app_params", {
          data: this.appParams
        });
      } else {
        console.error("Failed to find pixel content window");
        this.state = "failed";
        this.onFallbackOpen?.();
      }
    }, "sendAppParams"));
    __publicField(this, "setupExperienceListeners", /* @__PURE__ */ __name(({ onSuccess, onExit, onEvent, onRequestedUrl }) => {
      this.onMessage("event", {
        shouldUnsubscribe: false,
        onMessage: (data) => {
          const metadata = data;
          this.eventStreamListeners[metadata.eventName]?.forEach((cb) => cb?.());
          if (metadata.eventName === "success") {
            onSuccess?.();
          }
          if (metadata.eventName === "exit") {
            onExit?.(metadata.error);
          }
          if (metadata.eventName === "request_open_url") {
            onRequestedUrl?.(metadata.url);
          }
          onEvent?.(data);
        }
      });
    }, "setupExperienceListeners"));
    __publicField(this, "startDirectSignin", /* @__PURE__ */ __name((callback) => {
      const queryParams = new URLSearchParams();
      queryParams.set("appId", this.appId);
      queryParams.set("type", "direct");
      const directSigninUrl = `${this.host}/signin?${queryParams.toString()}`;
      const signinWinRef = openWindow(directSigninUrl, "popup");
      this.onMessage("signin_success", {
        onMessage: () => {
          signinWinRef?.close();
          callback();
        }
      });
    }, "startDirectSignin"));
    __publicField(this, "addEventStreamListener", /* @__PURE__ */ __name((name, cb) => {
      if (this.eventStreamListeners[name]) {
        this.eventStreamListeners[name]?.push(cb);
      } else {
        this.eventStreamListeners[name] = [
          cb
        ];
      }
    }, "addEventStreamListener"));
    __publicField(this, "removeEventStreamListener", /* @__PURE__ */ __name((name, callback) => {
      if (this.eventStreamListeners[name]) {
        const filteredListeners = this.eventStreamListeners[name]?.filter((cb) => cb !== callback);
        this.eventStreamListeners[name] = filteredListeners;
      }
    }, "removeEventStreamListener"));
    __publicField(this, "onMessage", /* @__PURE__ */ __name((...args) => {
      const unsubFxn = onBroadcastedPostMessage(args[0], {
        allowedOrigin: this.host,
        ...args[1]
      });
      this.unsubs.push(unsubFxn);
      return unsubFxn;
    }, "onMessage"));
    __publicField(this, "log", /* @__PURE__ */ __name((...args) => {
      if (this.debug) {
        console.log("[CBPAY]", ...args);
      }
    }, "log"));
    this.host = host;
    this.appId = appId;
    this.appParams = appParams;
    this.onReadyCallback = onReady;
    this.onFallbackOpen = onFallbackOpen;
    this.debug = debug || false;
    this.addPixelReadyListener();
    this.addErrorListener();
    this.embedPixel();
    setTimeout(() => {
      if (this.state !== "ready") {
        this.onFailedToLoad();
      }
    }, DEFAULT_MAX_LOAD_TIMEOUT);
  }
};
__name(CoinbasePixel, "CoinbasePixel");
function createPixel({ host, appId }) {
  const pixel = document.createElement("iframe");
  pixel.style.border = "unset";
  pixel.style.borderWidth = "0";
  pixel.style.width = "0";
  pixel.style.height = "0";
  pixel.style.height = "0";
  pixel.id = PIXEL_ID;
  const url = new URL(`${host}${PIXEL_PATH}`);
  url.searchParams.append("appId", appId);
  pixel.src = url.toString();
  return pixel;
}
__name(createPixel, "createPixel");
function openWindow(url, experience) {
  return window.open(url, "Coinbase", experience === "popup" ? `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, height=${PopupSizes.signin.height},width=${PopupSizes.signin.width}` : void 0);
}
__name(openWindow, "openWindow");

// src/utils/CBPayInstance.ts
var widgetRoutes = {
  buy: "/buy",
  checkout: "/checkout"
};
var CBPayInstance = class {
  constructor(options) {
    __publicField(this, "open", /* @__PURE__ */ __name(() => {
      const { widget, experienceLoggedIn, experienceLoggedOut, embeddedContentStyles, onExit, onSuccess, onEvent, onRequestedUrl, closeOnSuccess, closeOnExit } = this.options;
      this.pixel.openExperience({
        path: widgetRoutes[widget],
        experienceLoggedIn,
        experienceLoggedOut,
        embeddedContentStyles,
        onExit: () => {
          onExit?.();
          if (closeOnExit) {
            this.pixel.endExperience();
          }
        },
        onSuccess: () => {
          onSuccess?.();
          if (closeOnSuccess) {
            this.pixel.endExperience();
          }
        },
        onRequestedUrl,
        onEvent
      });
    }, "open"));
    __publicField(this, "destroy", /* @__PURE__ */ __name(() => {
      this.pixel.destroy();
    }, "destroy"));
    this.options = options;
    this.pixel = new CoinbasePixel({
      ...options,
      appParams: {
        widget: options.widget,
        ...options.appParams
      }
    });
    if (options.target) {
      const targetElement = document.querySelector(options.target);
      if (targetElement) {
        targetElement.addEventListener("click", this.open);
      }
    }
  }
};
__name(CBPayInstance, "CBPayInstance");

// src/onramp/initOnRamp.ts
var initOnRamp = /* @__PURE__ */ __name(({ experienceLoggedIn = "embedded", widgetParameters, ...options }, callback) => {
  const instance = new CBPayInstance({
    ...options,
    widget: "buy",
    experienceLoggedIn,
    appParams: widgetParameters,
    onReady: (error) => {
      if (error) {
        callback(error, null);
      } else {
        callback(null, instance);
      }
    },
    onFallbackOpen: () => {
      const url = generateOnRampURL({
        appId: options.appId,
        host: options.host,
        ...widgetParameters
      });
      window.open(url);
    }
  });
}, "initOnRamp");

// src/utils/events.ts
function broadcastEvent(sdkTarget, event) {
  broadcastPostMessage(sdkTarget, "event", {
    data: event
  });
}
__name(broadcastEvent, "broadcastEvent");
export {
  CBPayInstance,
  broadcastEvent,
  broadcastPostMessage,
  generateOnRampURL,
  getSdkTarget,
  initOnRamp,
  onBroadcastedPostMessage
};
//# sourceMappingURL=index.mjs.map